/**
 * Funds List Hook
 *
 * Fetches funds with filtering, sorting, and pagination using the Hono RPC client
 * with TanStack Query for caching and state management.
 */

import { useQuery, queryOptions, keepPreviousData } from "@tanstack/react-query";
import { api, unwrapResponse, ApiError } from "@/lib/api";
import { useDebounce } from "@/hooks/useDebounce";
import type { FundCategory, FundPlanType, FundRating } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

/**
 * Valid sort field options for fund search
 */
export type FundSortField =
  | "score"
  | "returns1Y"
  | "returns3Y"
  | "returns5Y"
  | "aum"
  | "expenseRatio"
  | "rating";

/**
 * Sort direction
 */
export type SortOrder = "asc" | "desc";

/**
 * Filter parameters for fund search
 */
export interface FundsFilterParams {
  /** Search query (searches fund name and AMC) */
  query?: string;
  /** Fund category filter */
  category?: FundCategory;
  /** Plan type filter (direct/regular) */
  plan?: FundPlanType;
  /** Rating filter */
  rating?: FundRating;
  /** Minimum AUM in crores */
  aumMin?: number;
  /** Maximum AUM in crores */
  aumMax?: number;
  /** Minimum expense ratio (percentage) */
  expenseRatioMin?: number;
  /** Maximum expense ratio (percentage) */
  expenseRatioMax?: number;
  /** Sort field */
  sortBy?: FundSortField;
  /** Sort direction */
  sortOrder?: SortOrder;
  /** Page number (1-indexed) */
  page?: number;
  /** Items per page */
  limit?: number;
}

/**
 * Fund list item returned in search results
 */
export interface FundListItem {
  isin: string;
  name: string;
  amcName: string;
  category: FundCategory;
  planType: FundPlanType;
  score: number;
  rating: FundRating;
  rank: number;
  totalFundsInCategory: number;
  returns1Y: number;
  returns3Y: number;
  returns5Y: number;
  aum: number;
  expenseRatio: number;
  exitLoad: string;
  nav: number;
  navDate: string;
}

/**
 * Applied filters in the response
 */
export interface AppliedFilters {
  query: string | null;
  category: FundCategory | null;
  plan: FundPlanType | null;
  rating: FundRating | null;
  aumMin: number | null;
  aumMax: number | null;
  expenseRatioMin: number | null;
  expenseRatioMax: number | null;
  sortBy: FundSortField;
  sortOrder: SortOrder;
}

/**
 * Pagination info in the response
 */
export interface PaginationInfo {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

/**
 * Funds list response data
 */
export interface FundsListData {
  funds: FundListItem[];
  pagination: PaginationInfo;
  filters: AppliedFilters;
}

// ============================================================================
// Query Keys
// ============================================================================

/**
 * Query key factory for funds queries
 */
export const fundsKeys = {
  all: ["funds"] as const,
  lists: () => [...fundsKeys.all, "list"] as const,
  list: (params: FundsFilterParams) => [...fundsKeys.lists(), params] as const,
};

// ============================================================================
// Query Options Factory
// ============================================================================

/**
 * Creates query options for funds list with the given filter params
 */
export function fundsListQueryOptions(params: FundsFilterParams = {}) {
  return queryOptions({
    queryKey: fundsKeys.list(params),
    queryFn: async (): Promise<FundsListData> => {
      // Build query params object, only including non-undefined values
      const queryParams: Record<string, string> = {};

      if (params.query) {
        queryParams.q = params.query;
      }
      if (params.category) {
        queryParams.category = params.category;
      }
      if (params.plan) {
        queryParams.plan = params.plan;
      }
      if (params.rating) {
        queryParams.rating = params.rating;
      }
      if (params.aumMin !== undefined) {
        queryParams.aumMin = String(params.aumMin);
      }
      if (params.aumMax !== undefined) {
        queryParams.aumMax = String(params.aumMax);
      }
      if (params.expenseRatioMin !== undefined) {
        queryParams.expenseRatioMin = String(params.expenseRatioMin);
      }
      if (params.expenseRatioMax !== undefined) {
        queryParams.expenseRatioMax = String(params.expenseRatioMax);
      }
      if (params.sortBy) {
        queryParams.sortBy = params.sortBy;
      }
      if (params.sortOrder) {
        queryParams.sortOrder = params.sortOrder;
      }
      if (params.page !== undefined) {
        queryParams.page = String(params.page);
      }
      if (params.limit !== undefined) {
        queryParams.limit = String(params.limit);
      }

      const response = await api.v1.funds.$get({
        query: queryParams,
      });

      return unwrapResponse<FundsListData>(response);
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
    placeholderData: keepPreviousData, // Keep previous data while fetching new data (for pagination)
  });
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Default debounce delay for search queries (300ms)
 */
const SEARCH_DEBOUNCE_DELAY = 300;

/**
 * Hook for fetching funds with filtering, sorting, and pagination
 *
 * Features:
 * - Debounced search query to prevent excessive API calls
 * - Proper cache keys based on all filter parameters
 * - Support for pagination with keepPreviousData for smooth transitions
 * - Loading, error, and data states
 *
 * @param params - Filter parameters
 * @param debounceDelay - Delay in ms for debouncing search query (default: 300ms)
 *
 * @example
 * ```tsx
 * function FundsList() {
 *   const [filters, setFilters] = useState({
 *     query: '',
 *     category: undefined,
 *     plan: 'direct' as const,
 *     sortBy: 'score' as const,
 *     sortOrder: 'desc' as const,
 *     page: 1,
 *     limit: 20,
 *   });
 *
 *   const { data, isLoading, error, refetch, isFetching } = useFunds(filters);
 *
 *   if (isLoading) return <FundsListSkeleton />;
 *   if (error) return <ErrorDisplay error={error} onRetry={refetch} />;
 *
 *   return (
 *     <div>
 *       {data.funds.map((fund) => (
 *         <FundCard key={fund.isin} data={fund} />
 *       ))}
 *       <Pagination
 *         page={data.pagination.page}
 *         totalPages={data.pagination.totalPages}
 *         onPageChange={(page) => setFilters({ ...filters, page })}
 *       />
 *     </div>
 *   );
 * }
 * ```
 */
export function useFunds(
  params: FundsFilterParams = {},
  debounceDelay: number = SEARCH_DEBOUNCE_DELAY
) {
  // Debounce the search query to prevent excessive API calls while typing
  const debouncedQuery = useDebounce(params.query, debounceDelay);

  // Build the final params with debounced query
  const queryParams: FundsFilterParams = {
    ...params,
    query: debouncedQuery,
  };

  return useQuery(fundsListQueryOptions(queryParams));
}

// ============================================================================
// Error Handling Utilities
// ============================================================================

/**
 * Type guard for ApiError
 */
export function isFundsApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Get user-friendly error message for funds errors
 */
export function getFundsErrorMessage(error: unknown): string {
  if (isFundsApiError(error)) {
    if (error.status >= 500) {
      return "Server error. Please try again later.";
    }
    if (error.status === 404) {
      return "Funds data not available.";
    }
    return error.message;
  }

  if (error instanceof Error) {
    if (error.message.includes("fetch")) {
      return "Network error. Please check your connection.";
    }
    return error.message;
  }

  return "An unexpected error occurred while loading funds.";
}
